import { CLUSTER_KEY_BY_LABEL, matchOpportunityToCluster } from '@/config/market-config'
import { normalizeForSearch } from '@/utils/strings'
import { useMemo } from 'react'
import type { AssetFilter, MarketFilter, SortDirection, SortField, UnifiedEarnFilter, YieldOpportunity } from '../types'
import { matchesAssetFilter } from '../utils/getAssetCategory'

export function useUnifiedEarnFilteredOpportunities(
  opportunities: YieldOpportunity[],
  filter: UnifiedEarnFilter,
): YieldOpportunity[] {
  return useMemo(() => {
    const filtered = opportunities.filter((opp) => {
      if (!matchesMarketFilter(opp, filter.markets)) return false
      if (!matchesAssetFilters(opp.assetSymbol, filter.assets)) return false
      if (!matchesTokenFilter(opp.assetSymbol, filter.assetFilter)) return false
      if (filter.inWalletOnly || filter.depositedOnly) {
        const matchesWallet = filter.inWalletOnly && hasWalletBalance(opp)
        const matchesDeposited = filter.depositedOnly && hasMeaningfulPosition(opp)
        if (!matchesWallet && !matchesDeposited) return false
      }
      if (filter.searchQuery && !matchesSearch(opp, filter.searchQuery)) return false
      return true
    })

    return sortOpportunities(filtered, filter.sortBy, filter.sortDirection)
  }, [opportunities, filter])
}

function matchesTokenFilter(assetSymbol: string | undefined, assetFilter: string[]): boolean {
  if (assetFilter.length === 0) return true
  if (!assetSymbol) return false
  return assetFilter.includes(assetSymbol)
}

/** Markets below $100k TVL are deprioritized - high APY on small markets dilutes immediately on deposit. USDXL is exempt. */
const QUALIFIED_MARKET_TVL_THRESHOLD_USD = 100_000

function isUsdxlAssetSymbol(symbol: string | undefined): boolean {
  return symbol?.toUpperCase() === 'USDXL'
}

function sortOpportunities(
  opportunities: YieldOpportunity[],
  sortBy: SortField,
  sortDirection: SortDirection,
): YieldOpportunity[] {
  return [...opportunities].sort((a, b) => {
    if (sortBy === 'tvl') {
      const diff = (a.tvlUsd?.toNumber() ?? 0) - (b.tvlUsd?.toNumber() ?? 0)
      return sortDirection === 'desc' ? -diff : diff
    }

    const aTvl = a.tvlUsd?.toNumber() ?? 0
    const bTvl = b.tvlUsd?.toNumber() ?? 0
    const aIsQualified =
      isUsdxlAssetSymbol(a.assetSymbol) || aTvl >= QUALIFIED_MARKET_TVL_THRESHOLD_USD
    const bIsQualified =
      isUsdxlAssetSymbol(b.assetSymbol) || bTvl >= QUALIFIED_MARKET_TVL_THRESHOLD_USD

    if (aIsQualified !== bIsQualified) {
      const qualifiedFirst = sortDirection === 'desc' ? -1 : 1
      return aIsQualified ? qualifiedFirst : -qualifiedFirst
    }

    const apyDiff = (a.supplyAPY ?? 0) - (b.supplyAPY ?? 0)
    return sortDirection === 'desc' ? -apyDiff : apyDiff
  })
}

function matchesMarketFilter(opp: YieldOpportunity, markets: MarketFilter[]): boolean {
  if (markets.length === 0) return true
  return markets.some((market) => {
    const clusterKey = CLUSTER_KEY_BY_LABEL.get(market)
    return clusterKey !== undefined && matchOpportunityToCluster(opp, clusterKey)
  })
}

function matchesAssetFilters(assetSymbol: string | undefined, assets: AssetFilter[]): boolean {
  if (assets.length === 0) return true
  return assets.some((asset) => matchesAssetFilter(assetSymbol, asset))
}

const MIN_USD_VALUE = 0.01

function hasWalletBalance(opp: YieldOpportunity): boolean {
  if (opp.walletBalance === undefined || opp.walletBalance.lte(0)) return false
  if (opp.token) {
    const usdValue = opp.token.toUSD(opp.walletBalance)
    return usdValue.gte(MIN_USD_VALUE)
  }
  return Number(opp.walletBalance) > 0
}

function hasMeaningfulPosition(opp: YieldOpportunity): boolean {
  if (!opp.hasPosition) return false
  if (opp.userDepositUsd !== undefined) {
    return opp.userDepositUsd.gte(MIN_USD_VALUE)
  }
  if (opp.userDeposit !== undefined && opp.token) {
    const usdValue = opp.token.toUSD(opp.userDeposit)
    return usdValue.gte(MIN_USD_VALUE)
  }
  return false
}

function matchesSearch(opp: YieldOpportunity, query: string): boolean {
  const normalizedQuery = normalizeForSearch(query)
  if (!normalizedQuery) return true

  const searchableFields = [opp.name, opp.symbol, opp.assetSymbol, opp.curatorName, opp.source].filter(Boolean)

  return searchableFields.some((field) => normalizeForSearch(field ?? '').includes(normalizedQuery))
}

export function sortByAPY(opportunities: YieldOpportunity[], descending = true): YieldOpportunity[] {
  return [...opportunities].sort((a, b) => {
    const apyA = a.supplyAPY ?? 0
    const apyB = b.supplyAPY ?? 0
    return descending ? apyB - apyA : apyA - apyB
  })
}

export function getLendingOnlyAPY(opp: YieldOpportunity): number {
  if (opp.sourceData.type === 'pooled' || opp.sourceData.type === 'elend') {
    return opp.sourceData.lendingAPY ?? opp.supplyAPY
  }
  return opp.supplyAPY
}

export function getTopEarningOpportunities(opportunities: YieldOpportunity[], count = 4): YieldOpportunity[] {
  const eligible = opportunities.filter((opp) => opp.source !== 'isolated')
  const sorted = sortByLendingAPYWithLiquidityPriority(eligible)
  return sorted.slice(0, count)
}

function sortByLendingAPYWithLiquidityPriority(opportunities: YieldOpportunity[]): YieldOpportunity[] {
  return [...opportunities].sort((a, b) => {
    const aTvl = a.tvlUsd?.toNumber() ?? 0
    const bTvl = b.tvlUsd?.toNumber() ?? 0
    const aAPY = getLendingOnlyAPY(a)
    const bAPY = getLendingOnlyAPY(b)
    const aIsQualified =
      isUsdxlAssetSymbol(a.assetSymbol) || aTvl >= QUALIFIED_MARKET_TVL_THRESHOLD_USD
    const bIsQualified =
      isUsdxlAssetSymbol(b.assetSymbol) || bTvl >= QUALIFIED_MARKET_TVL_THRESHOLD_USD

    if (aIsQualified !== bIsQualified) {
      return aIsQualified ? -1 : 1
    }

    return bAPY - aAPY
  })
}
